import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, Headset } from 'lucide-react';
import {
    doc, updateDoc, collection, addDoc,
    onSnapshot, query, orderBy, serverTimestamp
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';

// --- Claves de sesión -----------------------------------------------------
// ANON_KEY: identidad anónima persistente (capability inadivinable).
// ACTIVE_KEY: id del TICKET activo (doc autogenerado). Se limpia al resolverse.
const ANON_KEY = 'pa_support_anon_id';
const ACTIVE_KEY = 'pa_support_active_chat';

const getAnonId = () => {
    try {
        let id = localStorage.getItem(ANON_KEY);
        if (!id) {
            const rand = (window.crypto && window.crypto.randomUUID)
                ? window.crypto.randomUUID()
                : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
            id = `anon_${String(rand).replace(/[^A-Za-z0-9_-]/g, '')}`;
            localStorage.setItem(ANON_KEY, id);
        }
        return id;
    } catch {
        return `anon_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    }
};

// Mensaje de bienvenida del bot: solo UI, NO se persiste.
const WELCOME_MESSAGE = {
    id: '__welcome__',
    sender: 'bot',
    text: '¡Hola! 👋 Soy PrimeBot, tu asistente de PRIMEAPUESTA. ¿En qué puedo ayudarte hoy?'
};

// Respuestas rápidas para un ticket nuevo (sin mensajes aún).
const QUICK_REPLIES = ['💳 Depósitos', '🏦 Retiros', '❓ Otro motivo'];

const LiveChatWidget = ({ user, userProfile }) => {
    const auth = useAuth() || {};
    const currentUser = user || auth.user || null;
    const profile = userProfile || auth.userProfile || null;

    const [isOpen, setIsOpen] = useState(false);
    const [activeChatId, setActiveChatId] = useState(() => {
        try { return localStorage.getItem(ACTIVE_KEY) || null; } catch { return null; }
    });
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [isResolved, setIsResolved] = useState(false);
    const scrollRef = useRef(null);

    // Limpia el ticket activo (al resolverse o ante error de permisos) -> nuevo ticket.
    const clearActiveChat = useCallback(() => {
        setActiveChatId(null);
        setMessages([]);
        setIsResolved(false);
        try { localStorage.removeItem(ACTIVE_KEY); } catch { /* noop */ }
    }, []);

    // Suscripción al ticket activo: estado del doc (resolución) + mensajes.
    useEffect(() => {
        if (!activeChatId) {
            setMessages([]);
            return;
        }
        // Flag de cierre: evita que el listener de mensajes vuelva a poblar la
        // pantalla DESPUÉS de detectar la resolución (el mensaje de cierre del
        // agente dispararía un snapshot tardío).
        let closed = false;
        const chatRef = doc(db, 'support_chats', activeChatId);

        // Vigila el ciclo de vida: si el chat se resuelve (o desaparece), reiniciar.
        const unsubDoc = onSnapshot(
            chatRef,
            (snap) => {
                if (!snap.exists()) {
                    closed = true;
                    setMessages([]);     // vaciar explícitamente la pantalla
                    clearActiveChat();   // limpiar ticket + localStorage
                } else if (snap.data().status === 'resolved' && !closed) {
                    closed = true;
                    setIsResolved(true);
                    try { localStorage.removeItem(ACTIVE_KEY); } catch { /* noop */ }
                    // Esperar 10 segundos antes de limpiar la pantalla (si no la cierra antes)
                    setTimeout(() => {
                        setMessages([]);
                        clearActiveChat();
                    }, 10000);
                }
            },
            (err) => {
                // Permiso denegado / identidad cambiada -> empezar limpio.
                console.error('[support] chat doc:', err.message);
                closed = true;
                clearActiveChat();
            }
        );

        const q = query(
            collection(db, 'support_chats', activeChatId, 'messages'),
            orderBy('createdAt', 'asc')
        );
        const unsubMsgs = onSnapshot(
            q,
            (qs) => {
                if (closed) return; // ticket cerrado: no repoblar la pantalla
                const list = [];
                qs.forEach(d => list.push({ id: d.id, ...d.data() }));
                setMessages(list);
            },
            (err) => console.error('[support] messages:', err.message)
        );

        return () => { unsubDoc(); unsubMsgs(); };
    }, [activeChatId, clearActiveChat]);

    // Auto-scroll al último mensaje.
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, isOpen]);

    // Crea un TICKET nuevo (doc con id autogenerado) y lo fija como activo.
    const createChat = useCallback(async () => {
        const ref = await addDoc(collection(db, 'support_chats'), {
            userId: currentUser?.uid || null,
            userEmail: profile?.email || currentUser?.email || null,
            isAnonymous: !currentUser?.uid,
            anonId: currentUser?.uid ? null : getAnonId(),
            botActive: true,        // bot atiende por defecto
            status: 'open',
            unreadByAgent: false,
            createdAt: serverTimestamp(),
            lastMessageAt: serverTimestamp()
        });
        setActiveChatId(ref.id);
        try { localStorage.setItem(ACTIVE_KEY, ref.id); } catch { /* noop */ }
        return ref.id;
    }, [currentUser?.uid, currentUser?.email, profile?.email]);

    // Envía un mensaje del usuario. Acepta texto directo (respuestas rápidas).
    const sendMessage = useCallback(async (textArg) => {
        const fromInput = typeof textArg !== 'string';
        const text = (fromInput ? input : textArg).trim();
        if (!text || sending) return;

        setSending(true);
        if (fromInput) setInput('');
        try {
            // Crea un ticket nuevo si no hay uno activo (o tras resolución).
            let id = activeChatId;
            if (!id) id = await createChat();

            await addDoc(collection(db, 'support_chats', id, 'messages'), {
                text,
                sender: 'user',
                createdAt: serverTimestamp()
            });
            await updateDoc(doc(db, 'support_chats', id), {
                lastMessage: text,
                lastMessageAt: serverTimestamp(),
                unreadByAgent: true
            });
        } catch (e) {
            console.error('[support] send error:', e.message);
            if (fromInput) setInput(text); // restaurar para reintento
        } finally {
            setSending(false);
        }
    }, [input, sending, activeChatId, createChat]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const allMessages = [WELCOME_MESSAGE, ...messages];
    const showQuickReplies = messages.length === 0;

    return (
        <>
            {/* BURBUJA FLOTANTE (cerrada) */}
            {!isOpen && (
                <button
                    onClick={() => setIsOpen(true)}
                    aria-label="Abrir chat de soporte"
                    className="fixed z-[1100] right-4 bottom-[88px] lg:right-auto lg:left-4 lg:bottom-4
                        w-14 h-14 rounded-2xl bg-[#F59E0B] text-black shadow-[0_10px_30px_rgba(245,158,11,0.35)]
                        flex items-center justify-center transition-all duration-300 hover:bg-[#ffb02e]
                        hover:scale-105 active:scale-95 group"
                >
                    <MessageCircle size={24} strokeWidth={2.5} />
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-[#171717] animate-pulse" />
                </button>
            )}

            {/* PANEL DE CHAT (abierto) */}
            {isOpen && (
                <div className="fixed z-[1100] right-3 bottom-[84px] lg:right-auto lg:left-4 lg:bottom-4
                    w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] flex flex-col
                    bg-[#121212] border border-[#F59E0B]/15 rounded-[1.75rem] overflow-hidden
                    shadow-[0_20px_60px_rgba(0,0,0,0.7)] animate-in fade-in slide-in-from-bottom-4 duration-300 font-sans">

                    {/* Header */}
                    <div className="shrink-0 bg-[#181818] border-b border-white/5 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-[#F59E0B] flex items-center justify-center shadow-lg shadow-[#F59E0B]/20">
                                <Headset size={18} className="text-black" strokeWidth={2.5} />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-sm font-black text-white tracking-tight leading-none">Soporte Prime</span>
                                <span className="flex items-center gap-1.5 text-[10px] font-bold text-emerald-400 uppercase tracking-wider mt-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> En línea
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                if (isResolved) {
                                    setMessages([]);
                                    clearActiveChat();
                                }
                            }}
                            aria-label="Cerrar chat"
                            className="w-8 h-8 rounded-full bg-white/5 text-white/50 hover:text-white hover:bg-white/10 flex items-center justify-center transition-colors active:scale-95"
                        >
                            <X size={16} strokeWidth={2.5} />
                        </button>
                    </div>

                    {/* Mensajes */}
                    <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar p-4 space-y-3 bg-[#0F0F0F]">
                        {allMessages.map((m) => {
                            const isUser = m.sender === 'user';
                            return (
                                <div key={m.id} className={`flex items-end gap-2 ${isUser ? 'justify-end' : 'justify-start'}`}>
                                    {!isUser && (
                                        <div className="w-7 h-7 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/20 flex items-center justify-center shrink-0">
                                            <Bot size={14} className="text-[#F59E0B]" />
                                        </div>
                                    )}
                                    <div className={`max-w-[78%] px-3.5 py-2.5 text-[13px] leading-snug font-medium shadow-sm
                                        ${isUser
                                            ? 'bg-[#F59E0B] text-black rounded-2xl rounded-br-md'
                                            : 'bg-[#1E1E1E] text-neutral-100 border border-white/5 rounded-2xl rounded-bl-md'}`}>
                                        {m.text}
                                    </div>
                                </div>
                            );
                        })}

                        {/* Respuestas rápidas (solo en ticket nuevo / vacío) */}
                        {showQuickReplies && (
                            <div className="flex flex-wrap gap-2 pt-1 pl-9">
                                {QUICK_REPLIES.map(label => (
                                    <button
                                        key={label}
                                        onClick={() => sendMessage(label)}
                                        disabled={sending}
                                        className="px-3 py-1.5 rounded-full text-[12px] font-bold bg-[#1E1E1E] text-[#F59E0B]
                                            border border-[#F59E0B]/25 hover:bg-[#F59E0B]/10 transition-colors active:scale-95
                                            disabled:opacity-40 disabled:pointer-events-none"
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Input */}
                    <div className="shrink-0 bg-[#181818] border-t border-white/5 p-3">
                        {isResolved ? (
                            <div className="text-center text-sm text-emerald-400 font-bold bg-emerald-500/10 rounded-2xl py-3 animate-pulse">
                                El agente ha resuelto este chat.
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 bg-[#0F0F0F] border border-white/10 rounded-2xl px-2 py-1.5 focus-within:border-[#F59E0B]/40 transition-colors">
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Escribe tu mensaje..."
                                        maxLength={1000}
                                        className="flex-1 bg-transparent text-sm text-white placeholder:text-neutral-600 outline-none px-2 py-1.5 font-medium"
                                    />
                                    <button
                                        onClick={() => sendMessage()}
                                        disabled={!input.trim() || sending}
                                        aria-label="Enviar mensaje"
                                        className="w-9 h-9 rounded-xl bg-[#F59E0B] text-black flex items-center justify-center shrink-0
                                            transition-all hover:bg-[#ffb02e] active:scale-95 disabled:opacity-40 disabled:grayscale disabled:pointer-events-none"
                                    >
                                        <Send size={16} strokeWidth={2.5} />
                                    </button>
                                </div>
                                <p className="text-[9px] text-neutral-600 text-center mt-2 uppercase tracking-widest font-bold">
                                    PrimeBot · Respuesta automática
                                </p>
                            </>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default LiveChatWidget;
